import { inject, Injectable, signal } from "@angular/core";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import {
  ENRICHMENT_HEADER_TRANSPORT,
  EnrichmentDataService,
} from "../../../services/enrichment-data.service";
import { detailToModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { resolveOfferClassification } from "../../../mappers/poc.util";
import { EnrichmentPriceRow } from "./enrichment-list.types";

/**
 * Owns the inline preview modal's state and lifecycle.
 *
 * Pulled out of EnrichmentList so the list component only wires onClick →
 * `controller.open(item)` and renders the bound template. The controller
 * holds the four pieces of state the preview rail needs (item, loading,
 * error, return-focus target) and the request that pushes the package into
 * EnrichmentDataService for the embedded mobile-preview component.
 */
@Injectable()
export class EnrichmentListPreviewController {
  private readonly data = inject(EnrichmentDataService);
  private readonly enrichmentService = inject(EnrichmentService);

  previewItem: EnrichmentPriceRow | null = null;
  previewLoading = false;
  previewError: string | null = null;

  /** The price tier the embedded preview shows: the opened row's position in
   *  the loaded package (0 when the package does not list it), then whatever
   *  tier the user picks in the preview. Null until the package loads. */
  readonly priceIndex = signal<number | null>(null);

  /** Offer classification for the preview meta, resolved from the detail this
   *  controller fetches, the way the editor resolves it. */
  previewOfferClassification = "-";

  /** Element to refocus when the preview closes (so keyboard users return
   *  to the row that opened it). */
  private previewReturnFocus: HTMLElement | null = null;

  open(item: EnrichmentPriceRow): void {
    const active = document.activeElement;
    this.previewReturnFocus = active instanceof HTMLElement ? active : null;
    this.previewItem = item;
    this.previewError = null;
    this.previewLoading = true;
    this.previewOfferClassification = "-";
    this.priceIndex.set(null);
    this.data.setEnrichment(null);

    this.enrichmentService
      .getEnrichmentById(item.offeringId, { ...ENRICHMENT_HEADER_TRANSPORT })
      .subscribe({
        next: (res) => {
          if (this.previewItem?.key !== item.key) return;
          // The endpoint returns the raw flat `EnrichmentDetail`; the mobile
          // preview only reads via `isFlatEnrichmentModel(...).__detail`, so
          // wrap it the same way the editor shell does. Setting the bare
          // detail (the previous behavior) left the preview stage blank.
          const detail = res.data;
          const model = detail ? detailToModel(detail) : null;
          const index = (model?.productOfferingPrice ?? []).findIndex(
            (pop) => pop.id === item.priceId,
          );
          this.priceIndex.set(Math.max(0, index));
          this.data.setEnrichment(model);
          this.previewOfferClassification =
            resolveOfferClassification(detail) || "-";
          this.previewLoading = false;
        },
        error: () => {
          if (this.previewItem?.key !== item.key) return;
          this.previewLoading = false;
          this.previewError =
            "Failed to load preview. Open the editor for full details.";
        },
      });
  }

  close(): void {
    this.previewItem = null;
    this.previewLoading = false;
    this.previewError = null;
    this.previewOfferClassification = "-";
    this.priceIndex.set(null);
    this.data.setEnrichment(null);
    const target = this.previewReturnFocus;
    this.previewReturnFocus = null;
    if (target && typeof target.focus === "function") {
      setTimeout(() => target.focus(), 0);
    }
  }

  get isOpen(): boolean {
    return this.previewItem !== null;
  }
}
